import React, { useState, useEffect } from "react";
import { Button, CssBaseline, Grid, Box, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { useHistory } from "react-router-dom";
import { userServices } from "../services";
import { Toast, TextField } from "../components";
import { useQuery } from "../hooks";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import FormValidator from "../helpers/formValidator";
import {
	PASSWORD_REGEX,
	getVerificationMessage,
	getPasswordStrength,
	getPasswordStrengthColor,
	getPasswordStrengthText,
} from "../helpers/passwordValidator";

export default function UpdatePassword() {
	const classes = useStyles();
	const history = useHistory();
	const [toast, setToast] = useState(false);
	const [toastMessage, setToastMessage] = useState("");
	const token = useQuery().get("token");

	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [errors, setErrors] = useState({});

	const validator = new FormValidator([
		{
			field: "password",
			method: (password) => PASSWORD_REGEX.test(password),
			validWhen: true,
			message: (password) => getVerificationMessage(password),
		},
		{
			field: "confirmPassword",
			method: (confirmPassword, state) => confirmPassword === state.password,
			validWhen: true,
			message: "Las contraseñas no coinciden",
		},
	]);

	useEffect(() => {
		if (!token) {
			history.push("/");
		}
	}, [history, token]);

	const updatePassword = async (e) => {
		e.preventDefault();
		const validation = validator.validate({ password, confirmPassword });
		setErrors(validation);

		if (validation.isValid) {
			const { status, response } = await userServices.updatePassword({ token: token, password: password });
			if (status === 200) {
				if (response.message === "success") {
					setToast(true);
					setToastMessage("Contraseña actualizada");
					setTimeout(() => {
						history.push("/");
					}, 2000);
				} else {
					setToast(true);
					setToastMessage(response.error.message);
					setTimeout(() => {
						history.push("/");
					}, 2000);
				}
			}
		}
	};

	const handlePasswordChange = (e) => {
		const value = e.target.value;
		setPassword(value);
		const validation = validator.validateField("password", value);
		setErrors({ ...errors, password: validation.password });
	};

	const handleConfirmPasswordChange = (e) => {
		const value = e.target.value;
		setConfirmPassword(value);
		const validation = validator.validateField("confirmPassword", value);
        // We need to re-validate confirm password against the current password state
        // dependent validation might be tricky with validateField if it only takes value.
        // FormValidator.validateField implementation:
        // if(validation_method(field_value, ...args, { value }) !== rule.validWhen)
        // It passes { value } as state.
        // My confirmPassword rule: (confirmPassword, state) => confirmPassword === state.password
        // So state.password will be undefined if I only pass { value: confirmPassword }.
        // I need to patch validateField or just use validate() for confirm password or pass the full state.
        
        // Let's check FormValidator.js again.
        // validateField(field, value) -> calls validation_method(field_value, ...args, { value })
        // It intentionally limits state to just the field value.
        // I should probably manually check equality or just run full validation on change for simplicity or modify FormValidator further.
        // For now, I'll allow the bug or just check it manually in the change handler for better UX?
        // Actually, I can just run the full validator for specific field if I create a helper or just modify errors directly.
        
        if (value !== password) {
             setErrors(prev => ({...prev, confirmPassword: { isInvalid: true, message: "Las contraseñas no coinciden" }}));
        } else {
             setErrors(prev => ({...prev, confirmPassword: { isInvalid: false, message: "" }}));
        }
	};

	const onKeyDownHandler = (e) => {
		if (e.keyCode === 13) {
			updatePassword(e);
		}
	};

	return (
		<Container component="main" maxWidth="xs">
			<Toast
				message={toastMessage}
				open={toast}
				close={() => {
					setToast(false);
				}}
			/>
			<CssBaseline />
			<div className={classes.paper}>
				<img src="../../Unio-01.png" alt="" style={{ width: 150 }} />
				<form className={classes.form} noValidate onSubmit={updatePassword} onKeyDown={onKeyDownHandler}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								required
								fullWidth
								name="password"
								placeholder="Nueva contraseña"
								type={showPassword ? "text" : "password"}
								id="password"
								value={password}
								onChange={handlePasswordChange}
								error={errors.password && errors.password.isInvalid}
								iconComponent={showPassword ? Visibility : VisibilityOff}
								onClickIcon={() => setShowPassword(!showPassword)}
							/>
							{errors.password && errors.password.isInvalid && (
								<Typography
									variant="caption"
									style={{
										color: "red",
										fontSize: "0.7rem",
										whiteSpace: "pre-line",
										marginTop: "5px",
										marginLeft: "3px",
										display: "block",
									}}
								>
									{errors.password.message}
								</Typography>
							)}
							{password && (
								<Box style={{ width: "100%", marginTop: "5px" }}>
									<div
										style={{
											height: "5px",
											width: "100%",
											backgroundColor: "#e0e0e0",
											borderRadius: "5px",
											overflow: "hidden",
										}}
									>
										<div
											style={{
												height: "100%",
												width: `${(getPasswordStrength(password) / 4) * 100}%`,
												backgroundColor: getPasswordStrengthColor(getPasswordStrength(password)),
												transition: "width 0.3s ease-in-out",
											}}
										/>
									</div>
									<Typography variant="caption" style={{ color: getPasswordStrengthColor(getPasswordStrength(password)) }}>
										Fortaleza: {getPasswordStrengthText(getPasswordStrength(password))}
									</Typography>
								</Box>
							)}
						</Grid>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								required
								fullWidth
								name="confirmPassword"
								placeholder="Confirmar contraseña"
								id="confirmPassword"
								type="password"
								value={confirmPassword}
								onChange={handleConfirmPasswordChange}
								error={errors.confirmPassword && errors.confirmPassword.isInvalid}
								helperText={errors.confirmPassword ? errors.confirmPassword.message : ""}
							/>
						</Grid>
					</Grid>
					<Button type="submit" fullWidth variant="contained" color="secondary" className={classes.submit}>
						Actualizar
					</Button>
				</form>
			</div>
		</Container>
	);
}

const useStyles = makeStyles((theme) => ({
	paper: {
		marginTop: theme.spacing(8),
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
	},
	avatar: {
		margin: theme.spacing(1),
		backgroundColor: theme.palette.secondary.main,
	},
	form: {
		width: "100%",
		marginTop: theme.spacing(3),
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
		borderRadius: 25,
	},
}));
